import { computed, inject } from '@angular/core';
import { MbsLoginFacade } from '@facades/login.facade';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { AwsSetPayload, DataForLoginUser, LoginInfo } from 'mbs-ui-shared@models/login';
import { pipe } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';

type LoginState = {
  info: LoginInfo;
  isInfoLoading: boolean;
  alias: string;
  awsSetPayload: AwsSetPayload;
  dataForLoginUser?: DataForLoginUser;
};

const initialState: LoginState = {
  info: null,
  isInfoLoading: false,
  alias: null,
  awsSetPayload: null,
  dataForLoginUser: null
};

export const LoginStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed((store) => ({
    ip: computed(() => store.info()?.ipAddress),
    loginTitle: computed(() => store.info()?.pageText),
    isSignUpAllow: computed(() => store.info()?.isSignUpAllow),
    isExternalSignInAllow: computed(() => store.info()?.externalSignIn)
  })),
  withMethods((store, loginFacade = inject(MbsLoginFacade)) => ({
    setAWSSetPayload(awsSetPayload: AwsSetPayload): void {
      patchState(store, { awsSetPayload });
    },
    setAlias(alias: string): void {
      patchState(store, { alias });
    },
    loadInfo: rxMethod<void>(
      pipe(
        tap(() => patchState(store, { isInfoLoading: true })),
        switchMap(() => {
          return loginFacade.getInfo(store.alias()).pipe(
            tapResponse({
              next: (info) => patchState(store, { info, isInfoLoading: false }),
              error: () => patchState(store, { isInfoLoading: false })
            })
          );
        })
      )
    ),
    loadUserInfo: rxMethod<DataForLoginUser>(
      pipe(
        tap((data: DataForLoginUser) => patchState(store, { isInfoLoading: true, dataForLoginUser: data })),
        switchMap((data: DataForLoginUser) => {
          return loginFacade.getUserInfo(store.alias(), data).pipe(
            tapResponse({
              next: (info) => patchState(store, { info, isInfoLoading: false }),
              error: () => patchState(store, { isInfoLoading: false })
            })
          );
        })
      )
    )
  }))
);
